import { z } from "zod";
import { isCodeComponent, getSuperComponents } from "@/wab/shared/core/components";
import { ParamExportType } from "@/wab/shared/core/lang";
import { paramToVarName, toVarName } from "@/wab/shared/codegen/util";
import { makePlasmicIsPreviewRootComponent } from "@/wab/shared/codegen/react-p/serialize-utils";
import { FORCED_VARIANTS_PROP } from "@/wab/shared/forced-variants";
import { isWritableState } from "@/wab/shared/core/states";
import { isKnownPropParam, isKnownStateParam, isKnownFunctionType, type Component, type Site, type Param, type PropParam, type StateParam } from "@/wab/shared/model/classes";
import { isTextType, isNumType, isBoolType, isChoiceType, normalizeToChoiceObjects } from "@/wab/shared/model/model-util";
const scalar=z.union([z.string().max(16384),z.number().finite(),z.boolean()]);
const language=z.discriminatedUnion("mode",[
 z.object({mode:z.literal("authored")}).strict(),
 z.object({mode:z.literal("tag"),tag:z.string().min(1).max(100)}).strict(),
]);
/** First explicit scalar/initial-native increment. Remaining native structured
 * values/references/public state/providers are required under the same goal.
 * This metadata never evaluates a new expression or captures a live frame. */
const savedPublishingPrintContextSchema=z.object({
 version:z.literal(1),propDefaults:z.literal("native-preview-defaults"),
 props:z.array(z.object({paramId:z.string().min(1).max(128),value:scalar}).strict()).max(200),
 language,data:z.object({mode:z.literal("capture-native-reads")}).strict(),
 interaction:z.object({mode:z.literal("initial-native")}).strict(),
}).strict().superRefine((context,issues)=>{
 const ids=new Set<string>();
 for(const prop of context.props){if(ids.has(prop.paramId))issues.addIssue({code:z.ZodIssueCode.custom,message:"PRINT_CONTEXT_DUPLICATE_PARAM"});ids.add(prop.paramId);}
 // Sum bounded individual JSON fragments before forming any complete context
 // string. Preserve exact string bytes and normal JSON escaping, never NFC.
 const encode=new TextEncoder();let bytes=encode.encode(JSON.stringify({...context,props:[]})).byteLength;
 for(let i=0;i<context.props.length;i++){
  bytes+=encode.encode(JSON.stringify(context.props[i])).byteLength+(i===0?0:1);
  if(bytes>65536){issues.addIssue({code:z.ZodIssueCode.custom,message:"PRINT_CONTEXT_JSON_BYTE_LIMIT"});break;}
 }
 if(context.language.mode==="tag"){
  try{const tags=Intl.getCanonicalLocales(context.language.tag);if(tags.length!==1||tags[0].length>100)throw new Error("invalid");}
  catch{issues.addIssue({code:z.ZodIssueCode.custom,message:"PRINT_CONTEXT_LANGUAGE_INVALID"});}
 }
});
/** Abort raw size/shape failures before native Zod array traversal and dirty
 * refinements. Read own data descriptors only, never caller-defined getters.
 * No truncation/coercion: the original strict schema validates unchanged input. */
const RAW_MISSING = Symbol("missing-print-context-field");
function rawField(object:unknown,key:string):unknown {
 if(object===null||typeof object!=="object"||Array.isArray(object))return RAW_MISSING;
 const descriptor=Object.getOwnPropertyDescriptor(object,key);
 return descriptor&&"value" in descriptor?descriptor.value:RAW_MISSING;
}
function onlyRawDataFields(object:unknown,keys:string[]):boolean {
 if(object===null||typeof object!=="object"||Array.isArray(object))return false;
 for(const key in object){
  if(!Object.prototype.hasOwnProperty.call(object,key))continue;
  if(!keys.includes(key)||rawField(object,key)===RAW_MISSING)return false;
 }
 return keys.every(key=>rawField(object,key)!==RAW_MISSING);
}
function boundedRawPrintContext(raw:unknown):boolean {
 if(!onlyRawDataFields(raw,["version","propDefaults","props","language","data","interaction"]))return false;
 if(rawField(raw,"version")!==1||rawField(raw,"propDefaults")!=="native-preview-defaults")return false;
 const props=rawField(raw,"props");
 if(!Array.isArray(props))return false;
 const length=Object.getOwnPropertyDescriptor(props,"length")?.value;
 if(typeof length!=="number"||length>200)return false;
 for(let i=0;i<length;i++){
  const descriptor=Object.getOwnPropertyDescriptor(props,String(i));
  if(!descriptor||!("value" in descriptor))return false;
  const entry=descriptor.value;
  if(!onlyRawDataFields(entry,["paramId","value"]))return false;
  const id=rawField(entry,"paramId"),value=rawField(entry,"value");
  if(typeof id!=="string"||id.length===0||id.length>128)return false;
  if(typeof value==="string"){if(value.length>16384)return false;}
  else if(typeof value==="number"){if(!Number.isFinite(value))return false;}
  else if(typeof value!=="boolean")return false;
 }
 const language=rawField(raw,"language"),mode=rawField(language,"mode");
 if(mode==="authored"){if(!onlyRawDataFields(language,["mode"]))return false;}
 else if(mode==="tag"){
  if(!onlyRawDataFields(language,["mode","tag"]))return false;
  const tag=rawField(language,"tag");if(typeof tag!=="string"||tag.length===0||tag.length>100)return false;
 }else return false;
 const data=rawField(raw,"data"),interaction=rawField(raw,"interaction");
 return onlyRawDataFields(data,["mode"])&&rawField(data,"mode")==="capture-native-reads"&&onlyRawDataFields(interaction,["mode"])&&rawField(interaction,"mode")==="initial-native";
}
export const publishingPrintContextSchema=z.preprocess((raw,issues)=>{
 if(!boundedRawPrintContext(raw)){
  issues.addIssue({code:z.ZodIssueCode.custom,message:"PRINT_CONTEXT_RAW_SHAPE_OR_SIZE_INVALID",fatal:true});
  return z.NEVER;
 }
 return raw;
},savedPublishingPrintContextSchema);
export type PublishingPrintContext=z.infer<typeof publishingPrintContextSchema>;
export type PublishingPrintContextDiagnostic={code:"PRINT_CONTEXT_LANGUAGE_SHAPING_ONLY";level:"info"};
export type ResolvedPublishingPrintContext={initialProps:Record<string,string|number|boolean>;languageTag?:string;diagnostics:PublishingPrintContextDiagnostic[]};
export function newPublishingPrintContext():PublishingPrintContext{
 return {version:1,propDefaults:"native-preview-defaults",props:[],language:{mode:"authored"},data:{mode:"capture-native-reads"},interaction:{mode:"initial-native"}};
}
/** A registered writable code state can be initialized only through its
 * native uncontrolled alias. Parent state/callback execution remains native;
 * plain/Plume/private/read-only/implicit states are never scalar-coerced. */
export function isPublishingInitialCodeStateParam(component:Component,param:Param):param is StateParam {
 if(!isCodeComponent(component)||!isKnownStateParam(param)||param.exportType!==ParamExportType.External||!param.propEffect||param.propEffect===param.variable.name)return false;
 const states=component.states.filter(state=>state.param===param);
 if(states.length!==1||!isWritableState(states[0])||states[0].tplNode)return false;
 const onChange=states[0].onChangeParam;
 return !!onChange&&isKnownPropParam(onChange)&&isKnownFunctionType(onChange.type)&&component.params.includes(onChange)&&onChange.exportType===ParamExportType.External;
}
/** React consumes these config names or loses __proto__ while copying into its
 * own props object. Native runtime flags also belong to the capture owner.
 * Refuse unsupported inputs; computed keys alone cannot make them usable. */
export function isPublishingNativePropNameSupported(name:string):boolean {
 return !["__proto__","key","ref","__self","__source",makePlasmicIsPreviewRootComponent(),FORCED_VARIANTS_PROP].includes(name);
}
function scalarParam(component:Component,param:Param):param is PropParam|StateParam{
 return (isKnownPropParam(param)||isPublishingInitialCodeStateParam(component,param))&&param.exportType===ParamExportType.External&&(isTextType(param.type)||isNumType(param.type)||isBoolType(param.type)||isChoiceType(param.type));
}
/** UI inventory uses the same native scalar/export/initial-state predicate. */
export function supportedScalarPrintContextParams(component:Component):Array<PropParam|StateParam>{return component.params.filter((param):param is PropParam|StateParam=>scalarParam(component,param)&&isPublishingNativePropNameSupported(paramToVarName(component,param)));}
export function resolvePublishingPrintContext(site:Site,component:Component,raw?:PublishingPrintContext):ResolvedPublishingPrintContext{
 // Native reference-removal preflight uses projected component field objects
 // but retains actual Param identities; do not require component instanceof.
 if(!site.components.includes(component)||site.components.filter(other=>other.uuid===component.uuid).length!==1)throw new Error("PRINT_CONTEXT_COMPONENT_NOT_OWNED");
 const context=publishingPrintContextSchema.parse(raw??newPublishingPrintContext());
 const variantNames=new Set<string>([component,...getSuperComponents(component)].flatMap(owner=>owner.variantGroups.map(group=>toVarName(group.param.variable.name))));
 const entries:Array<[string,string|number|boolean]>=[];
 for(const override of context.props){
  const matches=component.params.filter(param=>param.uuid===override.paramId);
  if(matches.length!==1)throw new Error("PRINT_CONTEXT_PARAM_NOT_OWNED");
  const param=matches[0];
  if(!scalarParam(component,param))throw new Error("PRINT_CONTEXT_PROP_TYPE_UNSUPPORTED");
  const name=paramToVarName(component,param);
  if(!isPublishingNativePropNameSupported(name))throw new Error("PRINT_CONTEXT_NATIVE_PROP_NAME_UNSUPPORTED");
  if(variantNames.has(name))throw new Error("PRINT_CONTEXT_VARIANT_PROP_COLLISION");
  if(component.params.filter(other=>other.exportType===ParamExportType.External&&paramToVarName(component,other)===name).length!==1)throw new Error("PRINT_CONTEXT_EXPORTED_NAME_COLLISION");
  const value=override.value;
  const compatible=isTextType(param.type)?typeof value==="string":isNumType(param.type)?typeof value==="number":isBoolType(param.type)?typeof value==="boolean":isChoiceType(param.type)?(param.type.options.length===0?typeof value==="string":normalizeToChoiceObjects(param.type.options).some(option=>option.value===value)):undefined;
  if(compatible===undefined)throw new Error("PRINT_CONTEXT_PROP_TYPE_UNSUPPORTED");
  if(!compatible||(param.enumValues.length>0&&!param.enumValues.includes(value)))throw new Error("PRINT_CONTEXT_PROP_VALUE_INCOMPATIBLE");
  entries.push([name,value]);
 }
 // Supported constructor/quoted keys are own data properties. Unsupported
 // React/prototype keys were refused above, not silently dropped downstream.
 const initialProps=Object.fromEntries(entries),diagnostics:PublishingPrintContextDiagnostic[]=[];
 if(context.language.mode==="tag"){
  const languageTag=Intl.getCanonicalLocales(context.language.tag)[0];
  diagnostics.push({code:"PRINT_CONTEXT_LANGUAGE_SHAPING_ONLY",level:"info"});
  return {initialProps,languageTag,diagnostics};
 }
 return {initialProps,diagnostics};
}
